import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Sidebar — primary navigation for authenticated users.
 * Collapsible: when `collapsed`, it renders as an icon-only rail. State and the
 * matching content offset are owned by Layout.
 */
const Sidebar = ({ collapsed = false, onToggle }) => {
  const location = useLocation();
  const { isPlatformAdmin } = useAuth();

  const icon = (path, extra) => (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
      {extra && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={extra} />}
    </svg>
  );

  const navItems = [
    { name: 'Dashboard', path: '/dashboard', icon: icon('M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6') },
    { name: 'Analytics', path: '/analytics', icon: icon('M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z') },
    { name: 'Agent', path: '/chat', icon: icon('M13 10V3L4 14h7v7l9-11h-7z') },
    { name: 'Knowledge Base', path: '/knowledge', icon: icon('M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z') },
    { name: 'Conversations', path: '/conversations', icon: icon('M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z') },
    { name: 'Leads', path: '/leads', icon: icon('M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6 0a4 4 0 10-3-6.7') },
    { name: 'Trainer', path: '/trainer', icon: icon('M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z', 'M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z') },
  ];

  const footerItems = [
    isPlatformAdmin && {
      name: 'Platform Admin', path: '/admin', activeCls: 'bg-purple-600 text-white', idleCls: 'text-purple-300 hover:bg-gray-800 hover:text-white',
      icon: icon('M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'),
    },
    { name: 'Settings', path: '/settings', icon: icon('M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z', 'M15 12a3 3 0 11-6 0 3 3 0 016 0z') },
    { name: 'Widget Settings', path: '/settings/widget', icon: icon('M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z') },
    { name: 'Help', path: '/help', icon: icon('M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z') },
  ].filter(Boolean);

  const renderLink = (item) => {
    const isActive = location.pathname === item.path;
    const activeCls = item.activeCls || 'bg-blue-600 text-white';
    const idleCls = item.idleCls || 'text-gray-300 hover:bg-gray-800 hover:text-white';
    return (
      <Link
        key={item.name}
        to={item.path}
        title={collapsed ? item.name : undefined}
        aria-label={item.name}
        className={`group flex items-center rounded-md text-sm font-medium transition-colors ${
          collapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-3'
        } ${isActive ? activeCls : idleCls}`}
      >
        <span className={`flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`}>
          {item.icon}
        </span>
        {!collapsed && <span className="truncate">{item.name}</span>}
      </Link>
    );
  };

  return (
    <div className="h-full sm-glass border-r border-gray-800 shadow-lg flex flex-col">
      {/* Collapse toggle */}
      <div className={`flex border-b border-gray-800 py-3 ${collapsed ? 'justify-center px-2' : 'justify-end px-3'}`}>
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand' : 'Collapse'}
          className="flex items-center justify-center h-8 w-8 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <svg className={`h-5 w-5 transition-transform ${collapsed ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <div className="flex-1 px-2 py-4 overflow-y-auto overflow-x-hidden">
        <nav className="space-y-1">
          {navItems.map(renderLink)}
        </nav>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-800 p-2 space-y-1">
        {footerItems.map(renderLink)}
      </div>
    </div>
  );
};

export default Sidebar;
